import { useCallback } from "react";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {
  BulkResolveEventLocationsRowResult,
  BulkResolveEventLocationsRowStatus,
} from "@event-planner/shared";
import {
  locationImportSpec,
  type ParsedLocationImportRow,
} from "@event-planner/shared/imports";
import {
  ResolveAndImportDialog,
  type ProposalRow,
} from "../../../components/imports/ResolveAndImportDialog";
import {
  type BulkImportResultBucket,
} from "../../../components/BulkImportResultTable";
import {
  bulkCreateEventLocations,
  bulkResolveEventLocations,
} from "../../../services/eventLocationService";

interface ExcelLocationImportDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Fires after a non-zero import run completes so the parent can refetch. */
  onImported?: () => void;
}

type LocationProposal = ProposalRow<
  ParsedLocationImportRow,
  BulkResolveEventLocationsRowResult
>;

type WriteResult = Awaited<ReturnType<typeof bulkCreateEventLocations>>;

/**
 * Default include + locked policy for the reviewing phase. Exported so
 * unit tests can pin the policy without rendering the dialog.
 */
export function defaultIncludeForStatus(
  status: BulkResolveEventLocationsRowStatus | "invalid",
): { include: boolean; locked: boolean } {
  switch (status) {
    case "new":
      return { include: true, locked: false };
    case "matches_existing":
      return { include: false, locked: true };
    case "failed":
      return { include: false, locked: true };
    case "invalid":
      return { include: false, locked: true };
  }
}

/**
 * Bulk Locations import. Wraps the shared `ResolveAndImportDialog` shell
 * with location-specific resolve + write callables, the per-row card
 * renderer, and the done-phase result bucketing. The shell owns the
 * 5-phase state machine and the bulk include/skip controls.
 *
 * Imports write the typed values verbatim (`verified: false`,
 * `coordinates: null`); operators upgrade locations to verified
 * post-import by opening them in the LocationPicker.
 */
export function ExcelLocationImportDialog({
  open,
  onClose,
  eventId,
  onImported,
}: ExcelLocationImportDialogProps) {
  const resolveRows = useCallback(
    async (rows: ParsedLocationImportRow[]) => {
      const response = await bulkResolveEventLocations({ eventId, rows });
      return response.results;
    },
    [eventId],
  );

  const writeRows = useCallback(
    async (rows: ParsedLocationImportRow[]) => {
      return bulkCreateEventLocations({ eventId, rows });
    },
    [eventId],
  );

  const renderProposalCard = useCallback(
    (proposal: LocationProposal, onToggle: () => void) => (
      <ProposalCard proposal={proposal} onToggle={onToggle} />
    ),
    [],
  );

  return (
    <ResolveAndImportDialog<
      ParsedLocationImportRow,
      BulkResolveEventLocationsRowResult,
      WriteResult
    >
      open={open}
      onClose={onClose}
      onImported={onImported}
      title="Import Locations from Excel"
      entityNoun={{ singular: "Location", plural: "Locations" }}
      pickerHelpText={
        <>
          Upload an Excel file (.xlsx) with one row per location. Required:{" "}
          <strong>label</strong>. Imported rows are saved with the typed
          values; open a location in the dashboard afterwards to pin it on
          the map and verify against Apple Maps.
        </>
      }
      testIdSlug="location"
      spec={locationImportSpec}
      templateFileName="location-import-template.xlsx"
      sheetName="Locations"
      resolveRows={resolveRows}
      writeRows={writeRows}
      defaultIncludeForResolution={(resolution) =>
        defaultIncludeForStatus(resolution?.status ?? "invalid")
      }
      renderProposalCard={renderProposalCard}
      buildResultBuckets={({ writeResult, includedProposals, skippedProposals }) =>
        buildLocationBuckets(writeResult, includedProposals, skippedProposals)
      }
      importedAnythingFromResult={(result) => result.summary.created > 0}
    />
  );
}

// ---------------------------------------------------------------------------
// Per-row proposal card (location-specific layout)
// ---------------------------------------------------------------------------

function ProposalCard({
  proposal,
  onToggle,
}: {
  proposal: LocationProposal;
  onToggle: () => void;
}) {
  const { resolution, parsed, include, includeLocked, lineNumber } = proposal;

  // Format-invalid row (no resolution data , failed before resolve)
  if (!resolution) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5 }}
        data-testid={`proposal-row-${lineNumber}`}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            checked={false}
            disabled
            data-testid={`proposal-include-${lineNumber}`}
          />
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {parsed.data.label || <em>(missing label)</em>}
            </Typography>
            <Typography variant="caption" color="error.main">
              {parsed.message ?? "Format error"}
            </Typography>
          </Stack>
          <Chip size="small" label="Format error" color="error" />
        </Stack>
      </Paper>
    );
  }

  const addressLine = [
    parsed.data.address,
    parsed.data.city,
    parsed.data.stateOrRegion,
    parsed.data.country,
  ]
    .filter((p): p is string => Boolean(p))
    .join(", ");

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        opacity:
          includeLocked && resolution.status !== "matches_existing" ? 0.6 : 1,
      }}
      data-testid={`proposal-row-${lineNumber}`}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Checkbox
          checked={include}
          disabled={includeLocked}
          onChange={onToggle}
          data-testid={`proposal-include-${lineNumber}`}
        />
        <Stack spacing={0.5} flex={1} minWidth={0}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {resolution.typedLabel}
            </Typography>
            <StatusChip status={resolution.status} />
          </Stack>
          {addressLine && (
            <Typography variant="caption" color="text.secondary">
              {addressLine}
            </Typography>
          )}
          {resolution.message && (
            <Typography variant="caption" color="warning.main">
              {resolution.message}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function StatusChip({
  status,
}: {
  status: BulkResolveEventLocationsRowStatus;
}) {
  switch (status) {
    case "new":
      return <Chip size="small" label="New, ready" color="success" />;
    case "matches_existing":
      return <Chip size="small" label="Matches existing" color="info" />;
    case "failed":
      return <Chip size="small" label="Failed" color="error" />;
  }
}

// ---------------------------------------------------------------------------
// Done-phase result bucketing
// ---------------------------------------------------------------------------

interface ResultRow {
  lineNumber: number;
  label: string;
  typedLabel: string;
  status:
    | "success"
    | "duplicate"
    | "failed"
    | "invalid"
    | "skipped_existing";
  message: string | null;
}

function buildLocationBuckets(
  writeResult: WriteResult | null,
  includedProposals: LocationProposal[],
  skippedProposals: LocationProposal[],
): BulkImportResultBucket[] {
  const writeRows: ResultRow[] = includedProposals.map((p, i) => {
    const r = writeResult?.results[i];
    if (!r) {
      return {
        lineNumber: p.lineNumber,
        label: p.parsed.data.label,
        typedLabel: p.parsed.data.label,
        status: "failed",
        message: "Skipped at write phase",
      };
    }
    const status: ResultRow["status"] =
      r.status === "created"
        ? "success"
        : r.status === "duplicate_label"
          ? "duplicate"
          : "failed";
    return {
      lineNumber: p.lineNumber,
      label: r.label,
      typedLabel: r.typedLabel,
      status,
      message: r.message ?? null,
    };
  });

  const skipRows: ResultRow[] = skippedProposals.map((p) =>
    proposalToSkippedResult(p),
  );

  const all = [...writeRows, ...skipRows].sort(
    (a, b) => a.lineNumber - b.lineNumber,
  );

  const success = all.filter((r) => r.status === "success");
  // "Already Exists" merges write-phase duplicate-label hits AND
  // resolve-phase matches-existing skips , both are the same outcome
  // for the operator (existing location kept, nothing overwritten).
  const alreadyExists = all.filter(
    (r) => r.status === "duplicate" || r.status === "skipped_existing",
  );
  const failed = all.filter(
    (r) => r.status === "failed" || r.status === "invalid",
  );

  return [
    {
      id: "success",
      label: "Successfully Added",
      color: "success",
      description:
        "Saved with the typed values. Open each location to pin it on the map and verify the address.",
      rows: success.map((r) => ({
        id: `s-${r.lineNumber}`,
        primary: r.label,
        secondary: `Row ${r.lineNumber}`,
      })),
    },
    {
      id: "already_exists",
      label: "Already Exists",
      color: "warning",
      description:
        "A location with this label already exists in the event. Existing location was kept; nothing was overwritten.",
      rows: alreadyExists.map((r) => ({
        id: `e-${r.lineNumber}`,
        primary: r.label,
        secondary: r.message
          ? `Row ${r.lineNumber}: ${r.message}`
          : `Row ${r.lineNumber}`,
      })),
    },
    {
      id: "failed",
      label: "Failed",
      color: "error",
      description:
        "These rows were NOT written. Validation errors or unexpected callable failures. The per-row note explains which.",
      rows: failed.map((r) => ({
        id: `f-${r.lineNumber}`,
        primary: r.typedLabel,
        secondary: `Row ${r.lineNumber}: ${r.message ?? "unknown error"}`,
      })),
    },
  ];
}

function proposalToSkippedResult(p: LocationProposal): ResultRow {
  if (!p.resolution) {
    return {
      lineNumber: p.lineNumber,
      label: p.parsed.data.label || "(no label)",
      typedLabel: p.parsed.data.label || "(no label)",
      status: "invalid",
      message: p.parsed.message ?? "Format error",
    };
  }
  const status: ResultRow["status"] =
    p.resolution.status === "matches_existing"
      ? "skipped_existing"
      : "failed";
  return {
    lineNumber: p.lineNumber,
    label: p.resolution.proposal?.canonicalLabel ?? p.resolution.typedLabel,
    typedLabel: p.resolution.typedLabel,
    status,
    message: p.resolution.message ?? null,
  };
}
